import { usePost } from "../API";
import { useAsyncEffect } from "../Hooks/AsyncEffect";
import { Badge, Timeline } from "@aidkitorg/component-library";
import { RichText } from "@aidkitorg/types/lib/survey";
import React, { useContext, useState } from "react";
import InterfaceContext from "../Context";
import { HistoryListWrapper } from "./historyUtils";
import { useLocalizedStrings } from "../Localization";
import { TimelineItem } from "@aidkitorg/aidkit/lib/application/activity_log";

export function ActivityLog(props: { uid: string, relatedSupportCases?: { support_case: string; contact: string; channel: string }[] }) {
  const { uid, relatedSupportCases } = props;
  const getActivityLog = usePost("/applicant/get_activity_log");
  const [timeline, setTimeline] = useState<Record<string, TimelineItem[]>>({});
  const [timelineKeys, setTimelineKeys] = useState<string[]>([]);
  const [useUTC, setUseUTC] = useState(false);
  const context = useContext(InterfaceContext);
  const L = useLocalizedStrings();

  useAsyncEffect(async () => {
    const result = await getActivityLog({ applicant: uid, contacts: relatedSupportCases?.map((sc) => sc.contact) ?? [] });
    if (result?.timeline) {
      setTimeline(result.timeline);
      setTimelineKeys(Object.keys(result.timeline).sort((a, b) => new Date(b).getTime() - new Date(a).getTime()));
    }
  }, [uid, relatedSupportCases]);

  if (timelineKeys.length === 0) {
    return <div className="p-4 text-gray-500">{L.applicant.activity_log.no_activity_to_display}</div>;
  }

  const fieldUpdatedTemplate = L.applicant.activity_log.field_updated_to;
  const [beforeField, afterField] = fieldUpdatedTemplate.split("$field");
  const [beforeValue, afterValue] = afterField.split("$value");

  const userUpdatedTemplate = L.applicant.activity_log.user_updated;
  const [assigneeBeforeField, assigneeAfterField] = userUpdatedTemplate.split("$field");
  const [assigneeBeforeValue, assigneeAfterValue] = assigneeAfterField.split("$user");

  return (
    <HistoryListWrapper useUTC={useUTC} onCheckedChange={setUseUTC}>
      <div className="pt-4">
        <Timeline>
          {timelineKeys.flatMap((key, index) => {
            const items = timeline[key];
            const fieldChanges = items.filter((item): item is Extract<TimelineItem, { kind: 'field_change' }> => item.kind === 'field_change');
            const noteItems = items.filter((item): item is Extract<TimelineItem, { kind: 'note' | 'support_case_note' }> => item.kind === 'note' || item.kind === 'support_case_note');

            const nodes: React.ReactNode[] = [];
            if (fieldChanges.length > 0) {
              nodes.push(
                <Timeline.Item key={`${key}-${index}`} time={key}>
                  <ul className={`${fieldChanges.length > 1 ? 'list-disc pl-4 marker:text-gray-400' : 'pl-2'} space-y-1`}>
                    {fieldChanges.map((item, itemIndex) => (
                      <li key={`${item.key}-${item.value}-${itemIndex}`}>
                        {item.message ? (
                          <div className="font-bold">{item.message[context.lang]}</div>
                        )
                          : item.userLookup ? (
                            <div>
                              {assigneeBeforeField}
                              <span className="font-bold">{item.key}</span>
                              {assigneeBeforeValue}
                              <span className="font-bold">{item.userLookup}</span>
                              {assigneeAfterValue}
                            </div>
                          )
                            : (
                              <div>
                                {beforeField}
                                <span className="font-bold">{item.key}</span>
                                {beforeValue}
                                <span className="font-bold">{item.value}</span>
                                {afterValue}
                              </div>
                            )}
                      </li>
                    ))}
                  </ul>
                </Timeline.Item>
              );
            }
            noteItems.forEach((item, itemIndex) => {
              nodes.push(
                <Timeline.Item key={`${key}-note-${itemIndex}`} time={item.date}>
                  <div className="pl-2">
                    <div className="flex items-center">
                      <span className="font-bold">{L.applicant.activity_log.note_added}</span>
                      {item.kind === 'support_case_note' ? <Badge variant="info" className="ml-2 text-xs">Support Case</Badge> : null}
                    </div>
                    <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 mt-1.5 text-sm">
                      {item.note}
                    </div>
                    <div className="text-gray-500 text-xs mt-1.5">
                      {item.author}
                    </div>
                  </div>
                </Timeline.Item>
              );
            });
            return nodes;
          })}
        </Timeline>
      </div>
    </HistoryListWrapper>
  );
}
